import { useState } from "react";
import { useAuth } from "../../context/AuthProvider";
import { LucideIcon } from "./LucideIcon";
import "./reconcileRetryBanner.css";

/**
 * Reconciliation-pending banner (A3, LOCKED rule 7).
 *
 * When the server's email-indexed reunion had a per-session merge FAILURE, a
 * plan the customer owns couldn't be attached this request. Rather than fail
 * silently, we surface a small amber banner:
 * "We're still adding your plans to your account."
 * with a Retry action that re-fetches `/api/customer/context` (which re-runs the
 * merge + reunion server-side). Dismissible for the session — a dismissed banner
 * stays gone until the next app mount, so it doesn't nag on every route change.
 *
 * Mirrors `<MergeToast>` in App.tsx: reads auth state, renders nothing unless the
 * signed-in customer has `reconciliationPending`.
 */
export function ReconcileRetryBanner() {
  const { state, refresh } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [retrying, setRetrying] = useState(false);

  if (state.kind !== "customer" || !state.reconciliationPending || dismissed) {
    return null;
  }

  const onRetry = () => {
    if (retrying) return;
    setRetrying(true);
    // `refresh` re-pings /api/customer/context → server re-runs merge + reunion.
    // On success `reconciliationPending` clears and this unmounts; on continued
    // failure the flag stays and the banner re-arms (retrying resets).
    void refresh().finally(() => setRetrying(false));
  };

  return (
    <div className="fg fg-reconcile-banner" role="status" aria-live="polite">
      <div className="fg-reconcile-banner__inner">
        <span className="fg-reconcile-banner__icon" aria-hidden>
          <LucideIcon name="refresh-cw" size={18} strokeWidth={2.1} />
        </span>
        <span className="fg-reconcile-banner__msg">
          We&rsquo;re still adding your plans to your account.
        </span>
        <button
          type="button"
          className="fg-btn fg-btn--sm fg-reconcile-banner__retry"
          onClick={onRetry}
          disabled={retrying}>
          {retrying ? "Retrying…" : "Retry"}
        </button>
        <button
          type="button"
          className="fg-reconcile-banner__dismiss"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss">
          <LucideIcon name="x" size={16} strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
}
